import { useCallback, useMemo } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { Navbar as PlatformNavbar, useAuth } from '@jana-technology/platform-ui'

import type { IBaseSettings } from '$/types'
import {
  BaseSettingsGroup,
  useGetBaseSettingsQuery
} from 'app/services/api/basesettings'
import Filters from 'app/layout/Navbar/Filters'
import { useFeatureFlag } from 'common/contexts/FeatureFlagsContext'
import { useHasPermissions } from 'common/hooks/useHasPermissions'
import { APP_VERSION } from 'common/utils/versionConstants'

const selectCompanyNameFromBaseSettings = (
  baseSettings: IBaseSettings[] | undefined
): string | undefined => {
  return baseSettings
    ?.find((s) => s.groupName === BaseSettingsGroup.SiteSettings)
    ?.details.find((d) => d.key === 'CompanyName')?.value as string
}

const dataPaths = {
  basePath: '/data-management',
  dataView: '/data-management/data-view',
  importExport: '/data-management/import-export'
}

const riskPaths = {
  basePath: '/risk-management',
  currentState: '/risk-management/qra',
  savedStates: '/risk-management/assessments'
}

const regulatoryPaths = {
  basePath: '/regulatory-tools',
  hcaMca: '/regulatory-tools/hca-mca',
  phmsaExport: '/regulatory-tools/phmsa-export'
}

const Navbar = () => {
  const { pathname } = useLocation()
  const { user, isAuthenticated, loginWithRedirect, logout } = useAuth()
  const hasPermissions = useHasPermissions()

  const dataExportLabel = useFeatureFlag('timp.IsDataExportEnabled', true)
    ? 'Data Export'
    : 'Data Import & Export'

  const disableRegulatoryTools = useFeatureFlag(
    'timp.IsRegulatoryToolsDisabled',
    false
  )

  const { companyName } = useGetBaseSettingsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      companyName: selectCompanyNameFromBaseSettings(data)
    })
  })

  const isPathActive = useCallback(
    (path: string) => {
      return (
        Boolean(matchPath({ path: `${path}/*`, end: false }, pathname)) ||
        pathname === path
      )
    },
    [pathname]
  )

  const appVersion = useMemo(() => {
    const buildVersion = import.meta.env.LH_TIMP_BUILD_VERSION

    if (buildVersion === 'local') {
      return import.meta.env.VITE_APP_PACKAGE_VERSION
    }

    return APP_VERSION
  }, [])

  const links = useMemo(() => {
    const primaryLinks = [
      {
        label: 'Data Management',
        links: [
          {
            label: 'Data Views',
            link: dataPaths.dataView,
            active: isPathActive(dataPaths.dataView)
          },
          {
            label: dataExportLabel,
            link: dataPaths.importExport,
            active: isPathActive(dataPaths.importExport)
          }
        ]
      },
      {
        label: 'Risk Management',
        links: [
          {
            label: 'Current State',
            link: riskPaths.currentState,
            active:
              isPathActive(riskPaths.currentState) ||
              pathname === riskPaths.basePath
          },
          {
            label: 'Saved States',
            link: riskPaths.savedStates,
            active: isPathActive(riskPaths.savedStates)
          }
        ]
      }
    ]

    if (!disableRegulatoryTools) {
      primaryLinks.push({
        label: 'Regulatory Tools',
        links: [
          {
            label: 'Consequence Area & Class Location',
            link: regulatoryPaths.hcaMca,
            active: isPathActive(regulatoryPaths.hcaMca)
          },
          {
            label: 'DOT PHMSA Annual Report',
            link: regulatoryPaths.phmsaExport,
            active: isPathActive(regulatoryPaths.phmsaExport)
          }
        ]
      })
    }

    return primaryLinks
  }, [dataExportLabel, disableRegulatoryTools, isPathActive, pathname])

  const userMenuLinks = useMemo(() => {
    const menuLinks: Array<{
      label: string
      link: string
      active?: boolean
    }> = []

    if (hasPermissions(['healthcheck:read'])) {
      menuLinks.push({
        label: 'Configuration',
        link: '/settings/configuration',
        active: isPathActive('/settings/configuration')
      })
    }

    return menuLinks
  }, [hasPermissions, isPathActive])

  const handleAuthAction = () => {
    if (isAuthenticated) {
      void logout({
        logoutParams: {
          returnTo: globalThis.location.origin
        }
      })

      return
    }

    void loginWithRedirect()
  }

  return (
    <header className='z-9 sticky top-0'>
      <PlatformNavbar
        links={links}
        preserveQuery
        customActions={[<Filters key='filters' />]}
        userMenuOptions={{
          user: {
            name: user?.name ?? 'N/A',
            role: companyName,
            avatarSrc: user?.picture
          },
          links: userMenuLinks,
          onLogoutClick: handleAuthAction,
          appVersion
        }}
      />
    </header>
  )
}

export default Navbar
